/**
 * Tipos para estrutura de Matriz/Filial
 * Estes tipos são agnósticos ao schema e tratam campos opcionais com segurança
 */

/**
 * Representa o estado de uma empresa em relação a estrutura
 */
export interface CompanyStructureState {
  /** Se a empresa é uma Matriz */
  isMatriz: boolean;
  
  /** ID da Matriz (se for Filial) */
  matrizId?: number | null;
  
  /** Se a empresa é Controladora */
  isControladora: boolean;
  
  /** ID da Controladora (se for Subsidiária) */
  controladoraId?: number | null;
}

/**
 * Informações de uma Filial
 */
export interface FilialInfo {
  id: number;
  name?: string;
  nomeFantasia: string;
  razaoSocial?: string | null;
  cnpj: string;
  cidade: string;
  estado: string;
  status: 'active' | 'inactive';
  employeesCount?: number;
  createdAt?: string;
}

/**
 * Informações da Matriz
 */
export interface MatrizInfo {
  id: number;
  nomeFantasia: string;
  cnpj: string;
  cidade: string;
  estado: string;
  status: 'active' | 'inactive';
  filialCount?: number;
}

/**
 * Dados para criar uma Filial
 */
export interface CreateFilialData {
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  cnae?: string;
  atividadePrincipal?: string;
  grauRisco?: string;
  sector?: string;
  employeesCount?: number;
  email?: string;
  phone?: string;
  responsibleName?: string;
  /** Estes campos são preenchidos automaticamente */
  matrizId?: number;
  controladoraId?: number | null;
}

/**
 * Extrai o estado de estrutura de uma empresa
 * Trata campos opcionais com segurança
 */
export function extractStructureState(company: any): CompanyStructureState {
  return {
    isMatriz: Boolean(company?.isMatriz ?? false),
    matrizId: company?.matrizId ?? null,
    isControladora: Boolean(company?.isControladora ?? false),
    controladoraId: company?.controladoraId ?? null,
  };
}

/**
 * Verifica se uma empresa pode ter filiais
 */
export function canHaveFilials(company: any): boolean {
  const state = extractStructureState(company);
  return state.isMatriz === true;
}

/**
 * Verifica se uma empresa é uma filial
 */
export function isFilial(company: any): boolean {
  const state = extractStructureState(company);
  return state.matrizId !== null && state.matrizId !== undefined;
}

/**
 * Verifica se uma empresa é uma subsidiária de grupo
 */
export function isSubsidiaria(company: any): boolean {
  const state = extractStructureState(company);
  return state.controladoraId !== null && state.controladoraId !== undefined;
}

/**
 * Verifica se uma empresa é Matriz e Subsidiária (Subsidiária que é Matriz)
 */
export function isMatrizSubsidiaria(company: any): boolean {
  const state = extractStructureState(company);
  return state.isMatriz === true && state.controladoraId !== null && state.controladoraId !== undefined;
}
